/* eslint-disable sort-keys */
import { Injectable } from '@angular/core';
import type { MapboxMap } from '@polaris-customerweb/sharedcomponents.core';

/** RGBA Color */
export interface RGBAColor {
    /** Red - value should be integer between 0-255 */
    r: number;
    /** Green - value should be integer between 0-255 */
    g: number;
    /** Blue - value should be integer between 0-255 */
    b: number;
    /** Alpha - value should be float between 0-1 */
    a?: number;
}

/** Pulse Animation Options */
export interface PulseAnimationOptions {
    /** `Color` - an html string representing a color i.e. "#000000", "rgb(0, 0, 0)", "rgba(0, 0, 0, 1)" */
    color?: string;
    /** `Start Radius` - radius at the start of the animation in pixels */
    radiusStart?: number;
    /** `End Radius` - radius at end of the animation in pixels */
    radiusEnd?: number;
    /** `Start Opacity` - opacity at the start of the animation should be float between 0-1 */
    opacityStart?: number;
    /** `End Opacity` - opacity at the end of the animation should be float between 0-1 */
    opacityEnd?: number;
    /** `Duration` - duration of animation in seconds */
    duration?: number;
}

@Injectable({
    providedIn: 'root',
})
export class MapAnimationService {
    /**
     * Create a pulse dot animation object
     * @param mapboxMap Reference to a MapBox GL Map
     * @param options Options for the animation
     */
    createPulseDotAnimation(
        mapboxMap: MapboxMap,
        options: PulseAnimationOptions
    ) {
        const color = this.convertColorString(options.color || '');
        const radiusStart = options.radiusStart || 15;
        const radiusEnd = options.radiusEnd || 35;
        const opacityStart = options.opacityStart || 1.0;
        const opacityEnd = options.opacityEnd || 0.03;
        const durationSeconds = options.duration || 2;

        const pixelRatio = 2;
        const padding = 5;
        const canvasSize = (radiusEnd * 2 + padding * 2) * pixelRatio;

        const pulseDot = {
            context: null as CanvasRenderingContext2D | null,
            width: canvasSize,
            height: canvasSize,
            pixelRatio,
            data: new Uint8ClampedArray(canvasSize * canvasSize * 4),

            easeMethod: this.easeInSine.bind(this) as (
                time: number,
                start: number,
                end: number
            ) => number,

            onAdd() {
                const canvas = document.createElement('canvas');
                canvas.width = pulseDot.width;
                canvas.height = pulseDot.height;
                pulseDot.context = canvas.getContext('2d');
            },

            render() {
                const context = pulseDot.context;
                const duration = 1000 * durationSeconds;
                const time = (performance.now() % duration) / duration;
                const currentRadius = pulseDot.easeMethod(
                    time,
                    radiusStart * pixelRatio,
                    radiusEnd * pixelRatio
                );
                const currentOpacity: number = pulseDot.easeMethod(
                    time,
                    opacityStart,
                    opacityEnd
                );

                if (context) {
                    context.clearRect(0, 0, pulseDot.width, pulseDot.height);
                    context.beginPath();
                    context.arc(
                        pulseDot.width / 2,
                        pulseDot.height / 2,
                        currentRadius,
                        0,
                        Math.PI * 2
                    );
                    context.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${currentOpacity})`;
                    context.fill();

                    pulseDot.data = context.getImageData(
                        0,
                        0,
                        pulseDot.width,
                        pulseDot.height
                    ).data;
                }

                mapboxMap.triggerRepaint();

                return true;
            },
        };
        return pulseDot;
    }

    /**
     * Create a spinner animation object
     * @param mapboxMap Reference to a MapBox GL Map
     * @param color
     * @param canvasSize size of canvas
     */
    createSpinnerAnimation(
        mapboxMap: MapboxMap,
        color: string,
        canvasSize = 35
    ) {
        const spinner = {
            context: null as CanvasRenderingContext2D | null,
            pixelRatio: 2,
            width: canvasSize,
            height: canvasSize,
            data: new Uint8ClampedArray(canvasSize * canvasSize * 4),

            onAdd() {
                const canvas = document.createElement('canvas');
                canvas.width = spinner.width;
                canvas.height = spinner.height;
                spinner.context = canvas.getContext('2d', {
                    willReadFrequently: true,
                });
            },

            render() {
                const second = (performance.now() / 1000) % 60;
                const rotation = (second * 360 * Math.PI) / 180;

                const context = spinner.context as CanvasRenderingContext2D;
                context.clearRect(0, 0, spinner.width, spinner.height);
                //rotation should happen around center of canvas
                context.translate(spinner.width / 2, spinner.height / 2);
                context.rotate(rotation);

                //fill circle
                context.beginPath();
                context.arc(0, 0, spinner.width / 2, 0, 2 * Math.PI);
                context.fillStyle = color;
                context.fill();

                // spinner line, quarter circle with stroke
                context.beginPath();
                context.arc(0, 0, spinner.width / 3, 0, 0.5 * Math.PI);
                context.lineWidth = 5;
                context.strokeStyle = `#ffffff`;
                context.stroke();

                // undo canvas changes for next round
                context.rotate(-rotation);
                context.translate(-spinner.width / 2, -spinner.height / 2);

                spinner.data = context.getImageData(
                    0,
                    0,
                    spinner.width,
                    spinner.height
                ).data;
                mapboxMap.triggerRepaint();

                return true;
            },
        };
        return spinner;
    }

    /**
     * Ease Linear
     * @param time
     * @param start
     * @param end
     */
    // @ts-expect-error - no unused kept here for future use
    private easeLinear(time: number, start: number, end: number): number {
        const change = end - start;
        return change * time + start;
    }

    /**
     * Ease In Quad
     * @param time
     * @param start
     * @param end
     */
    // @ts-expect-error - no unused kept here for future use
    private easeInQuad(time: number, start: number, end: number): number {
        const change = end - start;
        return change * time * time + start;
    }

    /**
     * Ease Out Quad
     * @param time
     * @param start
     * @param end
     */
    // @ts-expect-error - no unused kept here for future use
    private easeOutQuad(time: number, start: number, end: number): number {
        const change = end - start;
        return -change * time * (time - 2) + start;
    }

    /**
     * Ease In & Out Quad
     * @param time
     * @param start
     * @param end
     */
    // @ts-expect-error - no unused kept here for future use
    private easeInOutQuad(time: number, start: number, end: number): number {
        const change = end - start;
        if (time < 0.5) {
            return change * time * time + start;
        } else {
            return -change * time * (time - 2) + start;
        }
    }

    /**
     * Ease In Sine
     * @param time
     * @param start
     * @param end
     */
    private easeInSine(time: number, start: number, end: number): number {
        const change = end - start;
        return -change * Math.cos(time * (Math.PI / 2)) + change + start;
    }

    /**
     * Ease Out Sine
     * @param time
     * @param start
     * @param end
     */
    // @ts-expect-error - no unused kept here for future use
    private easeOutSine(time: number, start: number, end: number): number {
        const change = end - start;
        return change * Math.sin(time * (Math.PI / 2)) + start;
    }

    /**
     * Ease In & Out Sine
     * @param time
     * @param start
     * @param end
     */
    // @ts-expect-error - no unused kept here for future use
    private easeInOutSine(time: number, start: number, end: number): number {
        const change = end - start;
        return (-change / 2) * (Math.cos(Math.PI * time) - 1) + start;
    }

    /**
     * Convert `colorString` into `RGBAColor`
     * @param colorString
     */
    private convertColorString(colorString: string): RGBAColor {
        let r = 0;
        let g = 0;
        let b = 0;
        const a = 0;

        if (colorString.includes('#')) {
            // 3 digits
            if (colorString.length === 4) {
                r = parseInt(colorString[1] + colorString[1], 16);
                g = parseInt(colorString[2] + colorString[2], 16);
                b = parseInt(colorString[3] + colorString[3], 16); // 6 digits
            } else if (colorString.length === 7) {
                r = parseInt(colorString[1] + colorString[2], 16);
                g = parseInt(colorString[3] + colorString[4], 16);
                b = parseInt(colorString[5] + colorString[6], 16);
            }
        }

        if (colorString.includes('rgb')) {
            // Implement Later
            console.error(
                'convertColor does not currently support rgb(#, #, #) values'
            );
        }

        if (colorString.includes('rgba')) {
            // Implement Later
            console.error(
                'convertColor does not currently support rgba(#, #, #, #) values'
            );
        }

        return { r, g, b, a };
    }
}
